import React from 'react';
import { Button, Form } from 'react-bootstrap';
import type {
  HubTagTemplate,
  HubTagTemplateUsedInStory,
} from '../../../dorian-shared/types/branch/HubTagTemplate';
import { useMemoryIconsContext } from '../../../contexts/MemoryIconsContext/MemoryIconsContext';
import { BadgeWithTooltip } from '../../ui/AvatarTool/BadgeWithTooltip/BadgeWithTooltip';
import { MemoryIconField } from '../Book/MemoryBank/MemoryFields/MemoryIconField';
import { TagChip } from '../../ui/TagChip/TagChip';
import { NAME_MAX, TEXT_MAX } from './tagTemplateValidation';
import css from './TagTemplateRow.module.scss';

type TagTemplateRowProps = {
  template: HubTagTemplate;
  nameError: string;
  contentError: string;
  onChange: (updated: HubTagTemplate) => void;
  onDelete: () => void;
  usedInStory: HubTagTemplateUsedInStory[];
};

const getTagTemplateUsedTooltipContent = (usedInStory: HubTagTemplateUsedInStory[]): JSX.Element => {
  const first = usedInStory[0];
  return (
    <>
      <b>Used in:</b>
      {` ${first.Book}`}
      <br />
      <b>Episode:</b>
      {` ${first.Episode}`}
      <br />
      <b>Revision:</b>
      {` ${first.Revision}`}
      <br />
      <b>Node:</b>
      {` ${first.Node}`}
      {usedInStory.length > 1 && (
        <>
          <br />
          <i>
            +
            {usedInStory.length - 1}
            {' '}
            more
          </i>
        </>
      )}
    </>
  );
};

export function TagTemplateRow({
  template, nameError, contentError, onChange, onDelete, usedInStory,
}: TagTemplateRowProps) {
  const isDeleteDisabled = usedInStory.length > 0;
  const { memoryIcons, createMemoryIcon } = useMemoryIconsContext();

  const hasError = !!nameError || !!contentError;

  const handleFieldChange = (field: keyof HubTagTemplate, value: string | number | null) => {
    onChange({ ...template, [field]: value });
  };

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const iconId = e.target.value ? Number(e.target.value) : null;
    handleFieldChange('iconId', iconId);
  };

  const selectedIcon = template.iconId != null
    ? memoryIcons.find((icon) => icon.id === template.iconId) ?? null
    : null;

  return (
    <div className={`${css.row} ${hasError ? css.rowError : ''}`} data-testid="tag-template-row">
      <div className={css.fields}>
        <Form.Group className={css.fieldName}>
          <Form.Label className="mb-0 small">Name</Form.Label>
          <Form.Control
            size="sm"
            value={template.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            isInvalid={!!nameError}
            maxLength={NAME_MAX}
            data-testid="tag-name-input"
          />
          <Form.Control.Feedback type="invalid" tooltip>
            {nameError}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className={css.fieldText}>
          <Form.Label className="mb-0 small">Display Text</Form.Label>
          <Form.Control
            size="sm"
            value={template.text ?? ''}
            onChange={(e) => handleFieldChange('text', e.target.value || null)}
            isInvalid={!!contentError}
            maxLength={TEXT_MAX}
            data-testid="tag-text-input"
          />
          <Form.Control.Feedback type="invalid" tooltip>
            {contentError}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className={css.fieldColor}>
          <Form.Label className="mb-0 small">Color</Form.Label>
          <div className={css.colorGroup}>
            <Form.Control
              type="color"
              size="sm"
              value={template.color ?? '#808080'}
              onChange={(e) => handleFieldChange('color', e.target.value)}
              className={css.colorSwatch}
              data-testid="tag-color-swatch"
            />
            <Form.Control
              size="sm"
              value={template.color ?? ''}
              onChange={(e) => handleFieldChange('color', e.target.value || null)}
              placeholder="#hex"
              className={css.colorInput}
              data-testid="tag-color-input"
            />
          </div>
        </Form.Group>

        <div className={css.fieldIcon}>
          <MemoryIconField
            id={`tag-icon-${template.id ?? 'new'}`}
            label="Icon (opt.)"
            memoryIcons={memoryIcons}
            onSave={createMemoryIcon}
            value={template.iconId != null ? String(template.iconId) : ''}
            onChange={handleIconChange}
            variant="outline-primary"
          />
        </div>

        <div className={css.fieldPreview}>
          <TagChip
            text={template.text}
            color={template.color}
            iconUrl={selectedIcon?.url ?? null}
            size="lg"
          />
        </div>

        <div className={css.fieldDelete}>
          <div className="position-relative">
            {isDeleteDisabled && (
              <div className="position-absolute" style={{ top: '-1rem', right: '0' }}>
                <BadgeWithTooltip
                  id={`delete-tag-${template.id ?? 'new'}`}
                  title="Can't be deleted"
                  content={getTagTemplateUsedTooltipContent(usedInStory)}
                />
              </div>
            )}
            <Button
              onClick={onDelete}
              variant="secondary"
              disabled={isDeleteDisabled}
              data-testid="tag-delete-btn"
            >
              X
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
